import React from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { useSimulation } from '../context/SimulationContext';
import { Shield, Zap, Lightbulb, AlertTriangle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const METER_CONFIG = {
  trust: {
    label: 'Trust',
    icon: Shield,
    description: 'The strength of your professional relationship with Ms. Ortega.',
    tip: 'Built through inquiry and empathy. Damaged by premature judgment.',
  },
  efficacy: {
    label: 'Efficacy',
    icon: Zap,
    description: 'Her belief in her ability to succeed as a teacher.',
    tip: 'Preserved through support. Damaged by harsh accountability.',
  },
  clarity: {
    label: 'Clarity',
    icon: Lightbulb,
    description: 'Shared understanding of the issue and path forward.',
    tip: 'Built by naming the impact clearly. Lost through vagueness.',
  },
  escalationRisk: {
    label: 'Escalation',
    icon: AlertTriangle,
    description: 'Likelihood the conversation becomes adversarial.',
    tip: 'Lower is better. Rises when trust falls or tone hardens.',
    inverse: true,
  },
};

// Animated number component
function AnimatedNumber({ value }) {
  const spring = useSpring(value, { stiffness: 100, damping: 20 });
  const display = useTransform(spring, (v) => Math.round(v));
  
  return <motion.span>{display}</motion.span>;
}

// Individual meter pill
function MeterPill({ meterKey, value, previewValue, showPreview }) {
  const config = METER_CONFIG[meterKey];
  const Icon = config.icon;
  
  const getColor = () => {
    const v = value;
    if (meterKey === 'escalationRisk') {
      if (v >= 70) return { bg: 'bg-[#DA291C]', text: 'text-[#DA291C]' };
      if (v >= 50) return { bg: 'bg-[#FFB81C]', text: 'text-[#FFB81C]' };
      return { bg: 'bg-[#78BE20]', text: 'text-[#78BE20]' };
    }
    if (v <= 29) return { bg: 'bg-[#DA291C]', text: 'text-[#DA291C]' };
    if (v <= 49) return { bg: 'bg-[#FFB81C]', text: 'text-[#FFB81C]' };
    return { bg: 'bg-[#78BE20]', text: 'text-[#78BE20]' };
  };
  
  const colors = getColor();
  const delta = showPreview && previewValue !== undefined ? previewValue - value : null;
  
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className="flex items-center gap-1.5 sm:gap-3 cursor-help"
            data-testid={`meter-${meterKey}`}
          >
            <Icon className="w-3 h-3 sm:w-4 sm:h-4 text-white/70" strokeWidth={1.5} />
            
            <div className="flex items-center gap-1 sm:gap-2">
              <span className="text-[9px] sm:text-xs text-white/70 uppercase tracking-wider font-medium hidden sm:inline">
                {config.label}
              </span>
              
              {/* Mini progress bar */}
              <div className="w-8 sm:w-16 h-1 sm:h-1.5 bg-white/15 overflow-hidden">
                <motion.div
                  className={colors.bg}
                  initial={{ width: 0 }}
                  animate={{ width: `${value}%` }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{ height: '100%' }}
                />
              </div>
              
              {/* Value */}
              <span className={`text-sm font-mono font-medium ${colors.text}`}>
                <AnimatedNumber value={value} />
              </span>
              
              {/* Preview delta */}
              {delta !== null && delta !== 0 && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`text-xs font-medium ${
                    config.inverse
                      ? (delta > 0 ? 'text-[#DA291C]' : 'text-[#78BE20]')
                      : (delta > 0 ? 'text-[#78BE20]' : 'text-[#DA291C]')
                  }`}
                >
                  {delta > 0 ? '+' : ''}{delta}
                </motion.span>
              )}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="bg-[#002B45] text-white border-none rounded-none p-4 max-w-xs shadow-2xl"
        >
          <p className="text-sm font-medium mb-1">{config.label}</p>
          <p className="text-xs text-white/60 mb-2">{config.description}</p>
          <p className="text-[10px] text-[#00A9E0] uppercase tracking-wider">
            Tip: {config.tip}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Fixed Top Bar - THE single source for HUD meters
export function FixedTopBar({ showPreview = false }) {
  const { state } = useSimulation();
  const { meters, previewMeters, simulation } = state;
  
  // Only show during simulation
  if (!simulation.active) return null;
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-40 bg-[#002B45] px-3 sm:px-8 py-2 sm:py-3"
      data-testid="fixed-top-bar"
      style={{ boxShadow: '0 2px 20px rgba(0, 43, 69, 0.5)' }}
    >
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        {/* Left: Stage indicator */}
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="text-[10px] text-white/70 uppercase tracking-widest font-medium hidden sm:inline">
            Stage {simulation.currentStage}
          </span>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4, 5].map((stage) => (
              <div
                key={stage}
                className={`w-4 sm:w-6 h-0.5 ${
                  stage < simulation.currentStage ? 'bg-[#78BE20]' :
                  stage === simulation.currentStage ? 'bg-[#00A9E0]' :
                  'bg-white/10'
                }`}
              />
            ))}
          </div>
        </div>
        
        {/* Right: Meters */}
        <div className="flex items-center gap-3 sm:gap-8">
          {Object.keys(METER_CONFIG).map((key) => (
            <MeterPill
              key={key}
              meterKey={key}
              value={meters[key]}
              previewValue={previewMeters?.[key]}
              showPreview={showPreview}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Compact HUD for non-simulation views (hub, theory, prep)
export function CompactHUD() {
  const { state, getMeterColor } = useSimulation();
  const { meters } = state;
  
  return (
    <div className="flex items-center gap-6" data-testid="compact-hud">
      {Object.entries(METER_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const color = getMeterColor(key);
        
        return (
          <TooltipProvider key={key} delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 cursor-help">
                  <Icon 
                    className={`w-4 h-4 ${
                      color === 'red' ? 'text-[#DA291C]' :
                      color === 'yellow' ? 'text-[#FFB81C]' :
                      'text-[#78BE20]'
                    }`} 
                    strokeWidth={1.5} 
                  />
                  <span className={`text-sm font-mono font-medium ${
                    color === 'red' ? 'text-[#DA291C]' :
                    color === 'yellow' ? 'text-[#FFB81C]' :
                    'text-[#002B45]'
                  }`}>
                    {meters[key]}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent 
                side="bottom" 
                className="bg-[#002B45] text-white border-none rounded-none p-3 shadow-2xl"
              >
                <p className="text-xs">{config.label}: {config.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

// Full HUD panel (for sidebar in non-simulation views)
export function HUDMeters({ showPreview = false, compact = false }) {
  const { state, isMeterCritical, getMeterColor } = useSimulation();
  const { meters, previewMeters } = state;

  if (compact) {
    return <CompactHUD />;
  }

  return (
    <div className="space-y-5" data-testid="hud-meters">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-[#475569]">
        Leadership Instruments
      </p>
      
      {Object.entries(METER_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const value = meters[key];
        const previewValue = previewMeters?.[key];
        const delta = showPreview && previewValue !== undefined ? previewValue - value : null;
        const isCritical = isMeterCritical(key);
        const color = getMeterColor(key);
        
        return (
          <TooltipProvider key={key} delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help" data-testid={`meter-${key}`}>
                  {/* Label Row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon 
                        className={`w-4 h-4 ${isCritical ? 'text-[#DA291C]' : 'text-[#002B45]'}`} 
                        strokeWidth={1.5}
                      />
                      <span className="text-xs font-medium text-[#002B45]">
                        {config.label}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-mono font-semibold ${
                        color === 'red' ? 'text-[#DA291C]' :
                        color === 'yellow' ? 'text-[#FFB81C]' :
                        'text-[#002B45]'
                      }`}>
                        {value}
                      </span>
                      
                      {delta !== null && delta !== 0 && (
                        <span className={`text-xs font-medium ${
                          config.inverse
                            ? (delta > 0 ? 'text-[#DA291C]' : 'text-[#78BE20]')
                            : (delta > 0 ? 'text-[#78BE20]' : 'text-[#DA291C]')
                        }`}>
                          {delta > 0 ? '+' : ''}{delta}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-1.5 w-full bg-slate-100 overflow-hidden">
                    <motion.div 
                      className={`h-full ${
                        color === 'red' ? 'bg-[#DA291C]' :
                        color === 'yellow' ? 'bg-[#FFB81C]' :
                        'bg-[#78BE20]'
                      } ${isCritical ? 'animate-pulse' : ''}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${value}%` }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent 
                side="left" 
                className="bg-[#002B45] text-white border-none rounded-none p-4 max-w-xs shadow-2xl"
              >
                <p className="text-sm font-medium mb-1">{config.label}</p>
                <p className="text-xs text-white/60 mb-2">{config.description}</p>
                <p className="text-[10px] text-[#00A9E0]">Tip: {config.tip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

// Inline HUD for simulation (NO LONGER USED - replaced by FixedTopBar)
export function InlineHUD() {
  // Deprecated - use FixedTopBar instead
  return null;
}

export default HUDMeters;
